import i18n from "../../../i18n";
import { incentiveTypeBadge } from "../../../data/options";
import React, { useMemo } from "react";
import { IconTransferIn, IconTransferOut, IconAlertTriangle } from "@tabler/icons";
import {
  Text,
  Badge,
  Group,
  Stack,
  Paper,
  Table,
  LoadingOverlay,
  Box,
  Alert,
} from "@mantine/core";
import EntityTabAnchor from "./EntityTabAnchor";
import { formatPayrollBidDate, formatPayrollBidHeldDate } from "./payrollSourceHelpers";

const getStatusColor = (status) => {
  switch (status) {
    case "SETTLED":
      return "green";
    case "HOLD":
      return "yellow";
    case "PENDING":
    default:
      return "gray";
  }
};

const getSourceLabel = (bid) => {
  if (bid.sourceType === "ADJUSTMENT") return "Adjustment";
  if (bid.sourceType === "CLIENTPAYMENT" || bid.debitNote)
    return "Client Payment";
  if (bid.sourceType === "COMMISSIONSTATEMENT" || bid.order)
    return "Comm. Statement";
  if (bid.sourceType === "INCENTIVE" || bid.incentive)
    return incentiveTypeBadge(bid.incentive?.incentiveType).label;
  return bid.sourceType || "-";
};

const getSourceColor = (bid) => {
  if (bid.sourceType === "ADJUSTMENT") return "red";
  if (bid.sourceType === "CLIENTPAYMENT" || bid.debitNote) return "blue";
  if (bid.sourceType === "COMMISSIONSTATEMENT" || bid.order) return "violet";
  if (bid.sourceType === "INCENTIVE" || bid.incentive)
    return incentiveTypeBadge(bid.incentive?.incentiveType).color;
  return "gray";
};

const ReleasedFromPoolTabContent = ({
  bids,
  loading,
  releasedCount,
  releasedTotalAmount,
  formatter,
  mode = "into",
}) => {
  const isFromMode = mode === "from";

  const statusBreakdown = useMemo(() => {
    const counts = { PENDING: 0, HOLD: 0, SETTLED: 0 };
    bids.forEach((bid) => {
      const status = bid.status || "PENDING";
      if (counts[status] != null) counts[status] += 1;
    });
    return counts;
  }, [bids]);

  const holdCount = statusBreakdown.HOLD;

  if (!loading && releasedCount === 0) {
    return (
      <Paper p="xl" withBorder>
        <Stack align="center" spacing="xs" py="xl">
          {isFromMode ? (
            <IconTransferOut size={48} color="gray" opacity={0.5} />
          ) : (
            <IconTransferIn size={48} color="gray" opacity={0.5} />
          )}
          <Text size="lg" weight={500} color="dimmed">
            {isFromMode
              ? "No Bids Released from this Payroll"
              : "No Released Bids from Pool"}
          </Text>
          <Text size="sm" color="dimmed" align="center">
            {isFromMode
              ? "Bids released from this payroll's holding pool to other payrolls will appear here."
              : "Bids released from the holding pool of previous payrolls will appear here."}
          </Text>
        </Stack>
      </Paper>
    );
  }

  const breakdownParts = [];
  if (statusBreakdown.PENDING > 0) {
    breakdownParts.push(`${statusBreakdown.PENDING} pending`);
  }
  if (statusBreakdown.HOLD > 0) {
    breakdownParts.push(`${statusBreakdown.HOLD} on hold`);
  }
  if (statusBreakdown.SETTLED > 0) {
    breakdownParts.push(`${statusBreakdown.SETTLED} settled`);
  }

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} overlayBlur={1} />
      <Text size="xs" color="dimmed" mb="md">
        {isFromMode
          ? "Bids that were held on this payroll and released to other payrolls. Read-only audit view."
          : "Bids moved from previous payroll holding pools into this payroll. After release they should be PENDING and ready to settle."}
      </Text>
      {!isFromMode && holdCount > 0 && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="yellow"
          mb="md"
          variant="light"
        >
          {holdCount} pool-released bid{holdCount !== 1 ? "s are" : " is"} on
          hold and will not be included in settlement.
        </Alert>
      )}
      {releasedCount > 0 && (
        <Group spacing="md" mb="md">
          <Text size="sm" weight={500}>
            {releasedCount} bid{releasedCount !== 1 ? "s" : ""}
            {breakdownParts.length > 0 && (
              <Text component="span" size="sm" color="dimmed">
                {" "}
                — {breakdownParts.join(", ")}
              </Text>
            )}
          </Text>
          <Text size="sm" weight={600} color="teal">
            {formatter.currency(releasedTotalAmount)}
          </Text>
        </Group>
      )}
      {bids.length > 0 && (
        <Table fontSize="xs" striped highlightOnHover withBorder>
          <thead>
            <tr>
              <th style={{ width: 40, textAlign: "center" }}>#</th>
              <th style={{ minWidth: 100 }}>{i18n.t("Status")}</th>
              <th style={{ minWidth: 150 }}>Bid</th>
              <th style={{ minWidth: 100 }}>{i18n.t("Order")}</th>
              <th style={{ minWidth: 100 }}>{i18n.t("Debit Note")}</th>
              <th style={{ minWidth: 100 }}>
                {isFromMode ? "Released To" : "Original Payroll"}
              </th>
              <th style={{ minWidth: 100 }}>{i18n.t("Held Date")}</th>
              <th style={{ minWidth: 100 }}>{i18n.t("Released Date")}</th>
              <th style={{ minWidth: 120 }}>{i18n.t("Wallet")}</th>
              <th style={{ width: 100, textAlign: "right" }}>{i18n.t("Amount")}</th>
            </tr>
          </thead>
          <tbody>
            {bids.map((bid, index) => {
              const walletUser = bid.wallet?.user;
              const walletUserGroup = bid.wallet?.userGroup;
              const isGroupBidWallet =
                bid.wallet?.type === "GROUP" || !!walletUserGroup;
              const walletName = isGroupBidWallet
                ? walletUserGroup?.name || bid.wallet?.name || "-"
                : walletUser?.name || bid.wallet?.name || "-";
              const staffId = isGroupBidWallet ? null : walletUser?.staffId;

              const payrollRef = isFromMode ? bid.payroll : bid.originalPayroll;

              return (
                <tr key={bid._id || index}>
                  <td style={{ textAlign: "center" }}>
                    <Text size="xs" color="dimmed">
                      {index + 1}
                    </Text>
                  </td>
                  <td>
                    <Badge
                      size="xs"
                      color={getStatusColor(bid.status)}
                      variant="filled"
                    >
                      {bid.status}
                    </Badge>
                  </td>
                  <td>
                    <Group noWrap spacing={1}>
                      <Badge
                        size="xs"
                        color={getSourceColor(bid)}
                        variant="light"
                        radius="0"
                      >
                        {getSourceLabel(bid)}
                      </Badge>
                    </Group>
                    <Text size="xs" weight={500}>
                      {bid.code}
                    </Text>
                  </td>
                  <td>
                    <EntityTabAnchor
                      entity="order"
                      id={bid.order?._id || bid.order}
                      label={bid.order?.code}
                    />
                  </td>
                  <td>
                    <EntityTabAnchor
                      entity="debitNote"
                      id={bid.debitNote?._id || bid.debitNote}
                      label={bid.debitNote?.code}
                    />
                  </td>
                  <td>
                    {payrollRef ? (
                      <Text size="xs" color="dimmed">
                        {payrollRef.code || "-"}
                      </Text>
                    ) : (
                      <Text size="xs" color="dimmed">
                        -
                      </Text>
                    )}
                  </td>
                  <td>
                    <Text size="xs" color="dimmed">
                      {formatPayrollBidHeldDate(bid)}
                    </Text>
                  </td>
                  <td>
                    <Text size="xs" color="dimmed">
                      {formatPayrollBidDate(bid.releasedFromPoolAt)}
                    </Text>
                  </td>
                  <td>
                    <Stack spacing={0}>
                      {staffId && (
                        <Text size="xs" color="dimmed">
                          {staffId}
                        </Text>
                      )}
                      <Text size="xs" weight={500} lineClamp={1}>
                        {walletName}
                      </Text>
                    </Stack>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Text
                      size="xs"
                      weight={600}
                      color={bid.commissionAmount >= 0 ? "teal" : "red"}
                    >
                      {formatter.currency(bid.commissionAmount)}
                    </Text>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Box>
  );
};

export default ReleasedFromPoolTabContent;
